import { pipe } from 'fp-ts/function';
import { FC } from 'react';
import { reduxForm } from 'redux-form';
import { Stack, Title } from '@optimus-web/core';
import { ColumnAlignment, MatrixMapping } from '@mews/b2b-ui';
import { LocalizedProperty, LocalizedText } from '@mews/framework';
import { GeneralEnterprise, GeneralPricingMode } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { doesServiceHaveOverridablePrice } from '@mews-commander/legacy-entities/src/ducks/services';
import { getProductCategory } from '@mews-commander/legacy-entities/src/ducks/productCategories';
import { getServicePriceableProductIdsByProductCategoryIdSortedByOrdering } from '@mews-commander/legacy-entities/src/selectors/products';
import { getCurrentEnterprise } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { Table } from '@mews-commander/ui/legacy';
import { useCommanderSelector } from '@mews-commander/utils';
import { PRODUCT_SERVICE_FORM_NAME } from '../../../constants';
import { ProductName } from '../../../../../components';
import ProductListProductUnitAmountField from '../../ProductListProductUnitAmountField';
import ProductListProductUnitCostField from '../../ProductListProductUnitCostField';
import ProductListProductTotalCost from '../../ProductListProductTotalCost';

interface OwnProps {
    productCategoryId: string | null;
    serviceId: string;
    allowNegativeValues: boolean;
}

const ProductServiceOrderProductListTablePure: FC<OwnProps> = ({
    productCategoryId,
    serviceId,
    allowNegativeValues,
}) => {
    const hasNetPricing = useCommanderSelector((state) => {
        const enterprise: GeneralEnterprise = getCurrentEnterprise(state);
        return enterprise.pricing === GeneralPricingMode.Net;
    });

    const productCategory = useCommanderSelector((state) =>
        productCategoryId ? getProductCategory(state, { serviceId, id: productCategoryId }) : null
    );

    const productIds = useCommanderSelector((state) =>
        getServicePriceableProductIdsByProductCategoryIdSortedByOrdering(state, { serviceId, productCategoryId })
    );

    const hasOverridablePrice = useCommanderSelector((state) =>
        doesServiceHaveOverridablePrice(state, { id: serviceId })
    );

    if (!productIds || !productIds.length) {
        return null;
    }

    const tableMapping: Array<MatrixMapping<string>> = [
        {
            valueKey: 0,
            title: <LocalizedText textKey={TextKey.Product} />,
            columnWidth: '25%',
            render: (productId) => <ProductName productId={productId} />,
        },
        {
            valueKey: 1,
            title: <LocalizedText textKey={hasNetPricing ? TextKey.UnitCostTaxExcluded : TextKey.UnitCost} />,
            columnWidth: '25%',
            render: (productId: string) => (
                <ProductListProductUnitCostField productId={productId} hasOverridablePrice={hasOverridablePrice} />
            ),
        },
        {
            valueKey: 2,
            title: <LocalizedText textKey={TextKey.Amount} />,
            columnWidth: '25%',
            render: (productId: string) => (
                <ProductListProductUnitAmountField productId={productId} allowNegativeValues={allowNegativeValues} />
            ),
        },
        {
            valueKey: 3,
            title: <LocalizedText textKey={hasNetPricing ? TextKey.CostTaxExcluded : TextKey.Cost} />,
            columnWidth: '25%',
            columnAlignment: ColumnAlignment.Right,
            render: (productId: string) => <ProductListProductTotalCost productId={productId} />,
        },
    ];

    return (
        <Stack vertical spacing="300">
            {productCategory && (
                <Title level={3}>
                    <LocalizedProperty property={productCategory.names} />
                </Title>
            )}
            <Table mapping={tableMapping} source={productIds} fixedHeader />
        </Stack>
    );
};

export const ProductServiceOrderProductListTable = pipe(
    ProductServiceOrderProductListTablePure,
    reduxForm({
        form: PRODUCT_SERVICE_FORM_NAME,
    })
);
